import type { InsiderTransaction } from "./types";

export type SignalKind =
  | "potential_buy"
  | "potential_sell"
  | "routine"
  | "no_market_signal"
  | "context_needed";

export interface SecTransactionCodeInfo {
  activityLabel: string;
  signalLabel: string;
  codeMeaning: string;
  signalKind: SignalKind;
}

export const SEC_TRANSACTION_CODE_INFO: Record<string, SecTransactionCodeInfo> = {
  P: {
    activityLabel: "Purchase (open/private)",
    signalLabel: "Potential buy signal",
    codeMeaning: "Purchase of securities, either open-market or private",
    signalKind: "potential_buy"
  },
  S: {
    activityLabel: "Sale (open/private)",
    signalLabel: "Potential sell signal",
    codeMeaning: "Sale of securities, either open-market or private",
    signalKind: "potential_sell"
  },
  V: {
    activityLabel: "Early voluntary report",
    signalLabel: "Needs source review",
    codeMeaning: "Transaction voluntarily reported earlier than required",
    signalKind: "context_needed"
  },
  A: {
    activityLabel: "Stock award/grant",
    signalLabel: "Routine compensation",
    codeMeaning: "Grant, award, or other Rule 16b-3 acquisition",
    signalKind: "routine"
  },
  D: {
    activityLabel: "Issuer disposition",
    signalLabel: "Routine/no market signal",
    codeMeaning: "Disposition to the issuer under Rule 16b-3",
    signalKind: "routine"
  },
  F: {
    activityLabel: "Tax/payment withholding",
    signalLabel: "Routine/no market signal",
    codeMeaning: "Tax or exercise-price payment using shares",
    signalKind: "routine"
  },
  I: {
    activityLabel: "Discretionary plan transaction",
    signalLabel: "Needs source review",
    codeMeaning: "Discretionary Rule 16b-3 transaction",
    signalKind: "context_needed"
  },
  M: {
    activityLabel: "Exercise/conversion",
    signalLabel: "Routine/uninformative",
    codeMeaning: "Exercise or conversion of derivative security",
    signalKind: "routine"
  },
  C: {
    activityLabel: "Derivative conversion",
    signalLabel: "Routine/no market signal",
    codeMeaning: "Conversion of derivative security",
    signalKind: "routine"
  },
  E: {
    activityLabel: "Short derivative expiration",
    signalLabel: "Routine/no market signal",
    codeMeaning: "Expiration of short derivative position",
    signalKind: "routine"
  },
  H: {
    activityLabel: "Long derivative expiration",
    signalLabel: "Routine/no market signal",
    codeMeaning: "Expiration or cancellation of long derivative position with value received",
    signalKind: "routine"
  },
  O: {
    activityLabel: "Out-of-money exercise",
    signalLabel: "Needs source review",
    codeMeaning: "Exercise of out-of-the-money derivative security",
    signalKind: "context_needed"
  },
  X: {
    activityLabel: "Option exercise",
    signalLabel: "Routine option exercise",
    codeMeaning: "Exercise of in-the-money or at-the-money derivative security",
    signalKind: "routine"
  },
  G: {
    activityLabel: "Gift transfer",
    signalLabel: "No market signal",
    codeMeaning: "Bona fide gift",
    signalKind: "no_market_signal"
  },
  L: {
    activityLabel: "Small acquisition",
    signalLabel: "Low-signal acquisition",
    codeMeaning: "Small acquisition under Rule 16a-6",
    signalKind: "context_needed"
  },
  W: {
    activityLabel: "Estate transfer",
    signalLabel: "No market signal",
    codeMeaning: "Transfer by will or inheritance",
    signalKind: "no_market_signal"
  },
  Z: {
    activityLabel: "Voting trust transfer",
    signalLabel: "No market signal",
    codeMeaning: "Deposit into or withdrawal from voting trust",
    signalKind: "no_market_signal"
  },
  J: {
    activityLabel: "Other transaction",
    signalLabel: "Needs source review",
    codeMeaning: "Other acquisition or disposition",
    signalKind: "context_needed"
  },
  K: {
    activityLabel: "Equity swap",
    signalLabel: "Needs source review",
    codeMeaning: "Equity swap or similar instrument",
    signalKind: "context_needed"
  },
  U: {
    activityLabel: "Tender/change-control disposition",
    signalLabel: "Needs source review",
    codeMeaning: "Disposition through tender in a change-of-control transaction",
    signalKind: "context_needed"
  }
};

export function getSecTransactionCodeInfo(code: string | null | undefined): SecTransactionCodeInfo | null {
  return SEC_TRANSACTION_CODE_INFO[(code ?? "").trim().toUpperCase()] ?? null;
}

export function isRoutineOrNoMarketCode(code: string | null | undefined): boolean {
  const signalKind = getSecTransactionCodeInfo(code)?.signalKind;
  return signalKind === "routine" || signalKind === "no_market_signal";
}

export function isContextNeededCode(code: string | null | undefined): boolean {
  return getSecTransactionCodeInfo(code)?.signalKind === "context_needed";
}

export function isCommonEquityTransaction(transaction: InsiderTransaction): boolean {
  const title = transaction.securityTitle.toLowerCase();
  const hasCommonEquityWord = /\b(common|ordinary|class [a-z])\b/.test(title);
  const hasNonCommonInstrument =
    /\b(note|notes|debt|bond|preferred|preference|option|warrant|unit|rsu|restricted stock unit|right to buy|convertible)\b/.test(
      title
    );
  return hasCommonEquityWord && !hasNonCommonInstrument && !transaction.isDerivative;
}
